import { api } from '../../lib/apiClient';
import { useAuthStore } from '../../store/authStore';

export const signatureKeys = {
  list: (params) => ['signatures', params ?? {}],
  detail: (id) => ['signature-detail', id],
  sourcePdf: (id) => ['signature-source', id],
  signerSession: (token) => ['signer-session', token],
  signerPdf: (token) => ['signer-pdf', token],
  waiver: (slug) => ['waiver', slug],
  waiverPdf: (slug) => ['waiver-pdf', slug],
};

/** Base path for the active workspace (same pattern as filesApi). */
function ws() {
  const workspaceId = useAuthStore.getState().workspaceId;
  if (!workspaceId) throw new Error('No active workspace selected');
  return `/workspaces/${workspaceId}`;
}

// ---- Sender-side (workspace-scoped) ----

export async function createDocument({ file, title, type, expiresAt }) {
  const form = new FormData();
  form.append('file', file);
  form.append('title', title);
  if (type) form.append('type', type);
  if (expiresAt) form.append('expiresAt', expiresAt);
  const { data } = await api.post(`${ws()}/signatures`, form);
  return data;
}

export async function listDocuments(params = {}) {
  const { data } = await api.get(`${ws()}/signatures`, { params });
  return data; // PaginatedResponse<DocumentListItem>
}

export async function getDocument(id) {
  const { data } = await api.get(`${ws()}/signatures/${id}`);
  return data; // { document, signers, fields }
}

export async function getSourcePdf(id) {
  const { data } = await api.get(`${ws()}/signatures/${id}/source`, {
    responseType: 'arraybuffer',
  });
  return data;
}

export async function updateDocument({ id, ...payload }) {
  const { data } = await api.patch(`${ws()}/signatures/${id}`, payload);
  return data;
}

export async function replaceFile({ id, file }) {
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.put(`${ws()}/signatures/${id}/file`, form);
  return data;
}

export async function replaceSigners({ id, signers }) {
  const { data } = await api.put(`${ws()}/signatures/${id}/signers`, { signers });
  return data;
}

export async function replaceFields({ id, pagesInPdf, fields }) {
  const { data } = await api.put(`${ws()}/signatures/${id}/fields`, { pagesInPdf, fields });
  return data;
}

export async function publishDocument({ id, emailData, autoReminders }) {
  const { data } = await api.post(`${ws()}/signatures/${id}/publish`, { emailData, autoReminders });
  return data; // { document } | { document, url } for waivers
}

export const setAutoReminders = async ({ id, enabled }) =>
  (await api.patch(`${ws()}/signatures/${id}/reminders`, { enabled })).data;

export const remindSigner = ({ id, signerId }) =>
  api.post(`${ws()}/signatures/${id}/signers/${signerId}/remind`);

export async function cloneDocument(id) {
  const { data } = await api.post(`${ws()}/signatures/${id}/clone`);
  return data;
}

export async function voidDocument(id) {
  const { data } = await api.post(`${ws()}/signatures/${id}/void`);
  return data;
}

export const deleteDocument = (id) => api.delete(`${ws()}/signatures/${id}`);

export async function downloadDocument(id, title) {
  const res = await api.get(`${ws()}/signatures/${id}/download`, { responseType: 'blob' });
  triggerBlobDownload(res.data, `${title}-signed.pdf`);
}

export async function downloadSubmission(id, signerId, name) {
  const res = await api.get(`${ws()}/signatures/${id}/submissions/${signerId}/download`, {
    responseType: 'blob',
  });
  triggerBlobDownload(res.data, `${name}-signed.pdf`);
}

// ---- Public agreement signer (token capability, no auth) ----

export async function getSignerSession(token) {
  const { data } = await api.get(`/signing/agreement/${token}`, { skipAuth: true });
  return data; // { document, signer, fields, progress }
}

export async function getSignerPdf(token) {
  const { data } = await api.get(`/signing/agreement/${token}/source`, {
    skipAuth: true,
    responseType: 'arraybuffer',
  });
  return data;
}

/**
 * Client-side audit capture for the Certificate of Completion — the signer's
 * time zone, device class and the moments they viewed, consented and finished
 * data entry. All advisory: the server clamps every timestamp to a plausible
 * window and falls back to its own clock, so nothing here is trusted as-is.
 * Mirrors `PublicFormPage`'s capture for form submissions.
 */
export function clientAudit(extra = {}) {
  return {
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    deviceType: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
    ...extra,
  };
}

export async function submitAgreement({ token, consent, values, audit = {} }) {
  const { data } = await api.post(
    `/signing/agreement/${token}`,
    { consent, values, ...clientAudit(audit) },
    { skipAuth: true },
  );
  return data; // { completed, downloadToken }
}

export async function downloadCompleted(token, title = 'document') {
  const res = await api.get(`/signing/agreement/${token}/completed`, {
    skipAuth: true,
    responseType: 'blob',
  });
  triggerBlobDownload(res.data, `${title}-signed.pdf`);
}

// ---- Public waiver (slug capability, no auth) ----

export async function getWaiver(slug) {
  const { data } = await api.get(`/signing/waiver/${slug}`, { skipAuth: true });
  return data; // { document, fields }
}

export async function getWaiverPdf(slug) {
  const { data } = await api.get(`/signing/waiver/${slug}/source`, {
    skipAuth: true,
    responseType: 'arraybuffer',
  });
  return data;
}

export async function submitWaiver({ slug, audit = {}, ...payload }) {
  const { data } = await api.post(
    `/signing/waiver/${slug}`,
    { ...payload, ...clientAudit(audit) },
    { skipAuth: true },
  );
  return data; // { downloadToken }
}

export async function downloadWaiverCopy(downloadToken, title = 'document') {
  const res = await api.get(`/signing/waiver/copy/${downloadToken}`, {
    skipAuth: true,
    responseType: 'blob',
  });
  triggerBlobDownload(res.data, `${title}-signed.pdf`);
}

/** SHA-256 of a file, computed in the browser: the file itself never leaves the computer. */
export async function sha256OfFile(file) {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Is this fingerprint a completed document sealed by MyDocUploads? */
export async function verifyFingerprint(sha256) {
  const { data } = await api.get(`/signing/verify/${sha256}`, { skipAuth: true });
  return data;
}

function triggerBlobDownload(blob, name) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
